/**
 * groupJobsByConversation — fold the polled query-job list into conversations.
 *
 * The sidebar renders threads, but the backend hands us JOBS: one row per
 * turn. Every follow-up therefore used to appear as its own sidebar thread.
 * `request_jsonb.conversation_id` (rag-api `JobSnapshot`) already identifies
 * the conversation, so this is a pure frontend fold — no API change.
 *
 * Conventions, matching ChatGPT/Claude:
 *   - the FIRST turn titles the row (the opening question is the subject;
 *     "ok, tell me more about it" is a terrible title)
 *   - the LATEST turn drives sort order, date bucketing and the click target
 *     (it is the state the user last saw)
 *
 * INVARIANT (load-bearing): a job with a missing, blank or non-string
 * `conversation_id` gets its OWN group. Bucketing those on a shared
 * `undefined` key would merge unrelated history into a single row — a worse
 * defect than the one this module fixes. The two key namespaces are prefixed
 * so a conversation_id can never collide with the per-job fallback.
 *
 * `conversation_id` is read exactly the way `useOpenJobFromHistory` reads it
 * (typeof-string guard over `request_jsonb`), so the two agree by construction.
 */

import type { JobSnapshot } from "@/types";

export interface ConversationGroup {
  /** Stable, collision-free identity for the rendered row. */
  key: string;
  /** The shared conversation id, or null for a standalone job. */
  conversationId: string | null;
  /** Every turn, oldest first. */
  jobs: JobSnapshot[];
  /** Oldest turn — titles the row. */
  firstJob: JobSnapshot;
  /** Newest turn — click target, sort key, date bucket. */
  latestJob: JobSnapshot;
  /** `jobs.length`; 1 for a standalone job. */
  turnCount: number;
}

/**
 * A turn whose `created_at` will not parse sorts as the OLDEST entry, so it
 * can never be mistaken for the conversation's latest state. Backend rows are
 * always ISO, so this is defence in depth rather than an expected path.
 */
function timeOf(job: JobSnapshot): number {
  const t = Date.parse(job.created_at);
  return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
}

export function conversationIdOf(job: JobSnapshot): string | null {
  const req = job.request_jsonb as Record<string, unknown> | null | undefined;
  if (!req || typeof req !== "object") return null;
  const raw = req.conversation_id;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function conversationKeyOf(job: JobSnapshot): string {
  const id = conversationIdOf(job);
  return id === null ? `job:${job.job_id}` : `conv:${id}`;
}

export function groupJobsByConversation(jobs: readonly JobSnapshot[]): ConversationGroup[] {
  const buckets = new Map<string, JobSnapshot[]>();
  for (const job of jobs) {
    const key = conversationKeyOf(job);
    const existing = buckets.get(key);
    if (existing) existing.push(job);
    else buckets.set(key, [job]);
  }

  const groups: ConversationGroup[] = [];
  for (const [key, bucket] of buckets) {
    // job_id tie-breaks equal timestamps so ordering never depends on the
    // order the poll happened to return.
    const ordered = [...bucket].sort((a, b) => timeOf(a) - timeOf(b) || a.job_id.localeCompare(b.job_id));
    const firstJob = ordered[0];
    const latestJob = ordered[ordered.length - 1];
    groups.push({
      key,
      conversationId: conversationIdOf(firstJob),
      jobs: ordered,
      firstJob,
      latestJob,
      turnCount: ordered.length,
    });
  }

  return groups.sort((a, b) => timeOf(b.latestJob) - timeOf(a.latestJob) || a.key.localeCompare(b.key));
}
